import React, { useMemo, useState } from "react";
import { Card, Form, Input, Popover, Select, Table, Tag, message } from "antd";
import { Button, Dialog, PlusIcon } from "evergreen-ui";
import { MDBCol, MDBRow } from "mdbreact";
import { useListVals } from "../../../Firebase/databaseHooks";
import Firebase from "../../../Firebase/Firebase";
import { getDatabase, ref, remove, set } from "firebase/database";
import { useObjectVal } from "react-firebase-hooks/database";
import TableActionMenu from "../../../Common/TableActionMenu";

const { TextArea } = Input;

const db = getDatabase(Firebase);

const TEMPLATE_TYPE_OPTIONS = [
	{ value: "welcome", label: "Welcome Email" },
	{ value: "passwordReset", label: "Password Reset Email" },
];

const TEMPLATE_HINTS = {
	welcome:
		"Available variables: {{appName}}, {{messageHeader}}, {{fullName}}, {{email}}, {{password}}",
	passwordReset:
		"Available variables: {{appName}}, {{messageHeader}}, {{fullName}}, {{resetLink}}",
};

const HTML_TEMPLATE_HINTS = {
	welcome:
		"Available HTML variables: {{appName}}, {{messageHeader}}, {{fullName}}, {{email}}, {{password}}, {{textBodyHtml}}",
	passwordReset:
		"Available HTML variables: {{appName}}, {{messageHeader}}, {{fullName}}, {{resetLink}}, {{textBodyHtml}}",
};

const TEMPLATE_SAVE_GUIDE = {
	welcome: {
		path: "talmidsdb/emailTemplates/welcome/<languageId>",
		variables: "{{appName}}, {{messageHeader}}, {{fullName}}, {{email}}, {{password}}",
		exampleSubject: "Welcome to {{appName}}",
	},
	passwordReset: {
		path: "talmidsdb/emailTemplates/passwordReset/<languageId>",
		variables: "{{appName}}, {{messageHeader}}, {{fullName}}, {{resetLink}}",
		exampleSubject: "{{appName}} Password Reset",
	},
};

const renderGuideContent = (guide) => (
	<div style={{ maxWidth: 420 }}>
		<div>
			One record is saved per template type and language. Saving again updates that same record.
		</div>
		<div>
			Required fields saved: <code>languageId</code>, <code>languageName</code>, <code>subject</code>, <code>text</code>, <code>updatedAt</code>
		</div>
		<div>
			Optional fields saved: <code>messageHeader</code>, <code>html</code>
		</div>
		<div>
			HTML can use <code>{"{{textBodyHtml}}"}</code> to render the Text Body content.
		</div>
		<div>
			Use placeholders exactly as written: <code>{guide.variables}</code>
		</div>
		<div>
			Example subject: <code>{guide.exampleSubject}</code>
		</div>
		<div>
			If <code>html</code> is left blank, the system builds the email from <code>text</code>.
		</div>
	</div>
);

const BASE_HTML_LAYOUT_TEMPLATES = [
	{
		key: "card",
		label: "Simple Card",
		description: "A clean card layout that renders the Text Body content.",
		html: `<div style="font-family: Arial, Helvetica, sans-serif; background-color: #f4f6f8; padding: 40px 20px;">
  <div style="max-width: 560px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; border: 1px solid #e0e0e0; overflow: hidden;">
    <div style="padding: 20px 32px; background-color: #f8fafc; border-bottom: 1px solid #e6edf5; font-size: 20px; font-weight: 600; color: #1f2937;">
      {{messageHeader}}
    </div>
    <div style="padding: 32px;">
      {{textBodyHtml}}
    </div>
  </div>
</div>`,
	},
	{
		key: "banner",
		label: "Header Banner",
		description: "A banner layout that keeps your Text Body as the main message.",
		html: `<div style="font-family: Arial, Helvetica, sans-serif; background-color: #eef3f8; padding: 32px 16px;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; border: 1px solid #dbe4ee;">
    <div style="background: #124c7c; padding: 24px 32px;">
      <div style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 600;">{{messageHeader}}</div>
    </div>
    <div style="padding: 32px;">
      {{textBodyHtml}}
    </div>
  </div>
</div>`,
	},
	{
		key: "outline",
		label: "Soft Outline",
		description: "A lightweight bordered layout that renders the Text Body content.",
		html: `<div style="font-family: Arial, Helvetica, sans-serif; background-color: #fff8ef; padding: 36px 18px;">
  <div style="max-width: 620px; margin: 0 auto; background-color: #ffffff; border: 1px solid #f0dfc7; border-radius: 10px; padding: 32px;">
    <div style="margin: 0 0 20px; color: #b26b00; font-size: 12px; letter-spacing: 1px; text-transform: uppercase;">{{messageHeader}}</div>
    {{textBodyHtml}}
  </div>
</div>`,
	},
];

const ENGLISH_FALLBACK_TEMPLATES = {
	welcome: {
		subject: "Welcome to {{appName}}",
		messageHeader: "Welcome to {{appName}}",
		text: `Hello {{fullName}},

Your {{appName}} account has been successfully created.

Login: {{email}}
Password: {{password}}`,
		html: `<div style="font-family: Arial, Helvetica, sans-serif; background-color: #f4f6f8; padding: 40px 20px;">
  <div style="max-width: 560px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; border: 1px solid #e0e0e0; padding: 32px;">
    <h2 style="margin-top: 0; color: #222;">Welcome to {{appName}}</h2>
    <p style="color: #555; line-height: 1.6;">Hello <strong>{{fullName}}</strong>,</p>
    <p style="color: #555; line-height: 1.6;">Your {{appName}} account has been successfully created.</p>
    <p style="color: #333; line-height: 1.6;"><strong>Login:</strong><br/>{{email}}</p>
    <p style="color: #333; line-height: 1.6;"><strong>Password:</strong><br/>{{password}}</p>
  </div>
</div>`,
	},
	passwordReset: {
		subject: "{{appName}} Password Reset",
		messageHeader: "Reset your password",
		text: `Hello {{fullName}},

You requested a password reset for your {{appName}} account.

Use this link to reset your password:
{{resetLink}}

If you did not request this, you can ignore this email.`,
		html: `<div style="font-family: Arial, Helvetica, sans-serif; background-color: #f4f6f8; padding: 40px 20px;">
  <div style="max-width: 560px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; border: 1px solid #e0e0e0; padding: 32px;">
    <h2 style="margin-top: 0; color: #222;">Reset your password</h2>
    <p style="color: #555; line-height: 1.6;">Hello <strong>{{fullName}}</strong>,</p>
    <p style="color: #555; line-height: 1.6;">You requested a password reset for your {{appName}} account.</p>
    <p style="margin: 24px 0;">
      <a href="{{resetLink}}" style="display:inline-block;padding:10px 16px;border-radius:4px;background:#1976d2;color:#ffffff;text-decoration:none;">
        Reset Password
      </a>
    </p>
    <p style="color: #777; font-size: 13px; line-height: 1.6;">If the button does not work, copy and paste this link into your browser:<br/>{{resetLink}}</p>
    <p style="color: #555; line-height: 1.6;">If you did not request this, you can ignore this email.</p>
  </div>
</div>`,
	},
};

const HTML_BODY_TEMPLATE_OPTIONS = {
	welcome: BASE_HTML_LAYOUT_TEMPLATES,
	passwordReset: BASE_HTML_LAYOUT_TEMPLATES,
};

const normalizeLanguageToken = (value) =>
	String(value || "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "");

const escapeHtml = (value) =>
	String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

const buildHtmlParagraphsFromText = (text) =>
	String(text || "")
		.replace(/\r\n/g, "\n")
		.trim()
		.split(/\n{2,}/)
		.map((paragraph) => paragraph.trim())
		.filter(Boolean)
		.map(
			(paragraph) =>
				`<p style="color: #555; line-height: 1.6;">${escapeHtml(paragraph).replace(
					/\n/g,
					"<br/>"
				)}</p>`
		)
		.join("") ||
	`<p style="color: #555; line-height: 1.6;">Add your Text Body content to preview this layout.</p>`;

const injectTextBodyHtml = (html, text) =>
	String(html || "").replace(/\{\{\s*textBodyHtml\s*\}\}/g, buildHtmlParagraphsFromText(text));

const upgradeLegacyMessageHeaderPlaceholder = (html) =>
	String(html || "")
		.replace(
			/(<div style="padding: 20px 32px; background-color: #f8fafc; border-bottom: 1px solid #e6edf5; font-size: 20px; font-weight: 600; color: #1f2937;">)\s*\{\{\s*appName\s*\}\}(\s*<\/div>)/g,
			"$1{{messageHeader}}$2"
		)
		.replace(
			/(<div style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 600;">)\s*\{\{\s*appName\s*\}\}(\s*<\/div>)/g,
			"$1{{messageHeader}}$2"
		)
		.replace(
			/(<div style="margin: 0 0 20px; color: #b26b00; font-size: 12px; letter-spacing: 1px; text-transform: uppercase;">)\s*\{\{\s*appName\s*\}\}(\s*<\/div>)/g,
			"$1{{messageHeader}}$2"
		);

const injectPreviewVariable = (html, key, value) =>
	upgradeLegacyMessageHeaderPlaceholder(String(html || "")).replace(
		new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "g"),
		String(value || `{{${key}}}`)
	);

const buildHtmlPreviewDocument = (html) => `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Email Preview</title>
  </head>
  <body style="margin: 0; background: #f5f7fa;">
    ${html}
  </body>
</html>`;

const EmailTemplatesLayout = ({
	canCreate = true,
	canUpdate = true,
	canDelete = true,
}) => {
	const [languages = [], languagesLoading] = useListVals(ref(db, "talmidsdb/languages"));
	const [templatesValue = {}, templatesLoading] = useObjectVal(
		ref(db, "talmidsdb/emailTemplates")
	);
	const [showDialog, setShowDialog] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [previewTemplate, setPreviewTemplate] = useState(null);
	const [saving, setSaving] = useState(false);
	const [form] = Form.useForm();

	const languageOptions = useMemo(
		() =>
			(languages || [])
				.filter((item) => item?.id && (item?.languageName || item?.language))
				.map((item) => ({
					value: item.id,
					label: item.languageName || item.language,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[languages]
	);

	const templateRows = useMemo(() => {
		return TEMPLATE_TYPE_OPTIONS.flatMap((templateOption) =>
			languageOptions.map((languageOption) => {
				const storedTemplate =
					templatesValue?.[templateOption.value]?.[languageOption.value] || null;
				const isEnglishLanguage =
					normalizeLanguageToken(languageOption.label) === "english";
				const fallbackTemplate = isEnglishLanguage
					? ENGLISH_FALLBACK_TEMPLATES[templateOption.value]
					: null;

				return {
					key: `${templateOption.value}-${languageOption.value}`,
					templateType: templateOption.value,
					languageId: languageOption.value,
					languageName: languageOption.label,
					subject: storedTemplate?.subject || fallbackTemplate?.subject || "",
					messageHeader:
						storedTemplate?.messageHeader || fallbackTemplate?.messageHeader || "",
					text: storedTemplate?.text || fallbackTemplate?.text || "",
					html: storedTemplate?.html || fallbackTemplate?.html || "",
					source: storedTemplate ? "custom" : fallbackTemplate ? "fallback" : "empty",
				};
			})
		);
	}, [languageOptions, templatesValue]);

	const openCreateDialog = () => {
		if (!canCreate) {
			message.error("You do not have permission to create email templates.");
			return;
		}

		setEditingTemplate(null);
		form.resetFields();
		setShowDialog(true);
	};

	const openEditDialog = (record) => {
		if (!canUpdate) {
			message.error("You do not have permission to update email templates.");
			return;
		}

		setEditingTemplate(record);
		form.setFieldsValue({
			templateType: record.templateType,
			languageId: record.languageId,
			subject: record.subject,
			messageHeader: record.messageHeader,
			text: record.text,
			html: record.html,
		});
		setShowDialog(true);
	};

	const handleDelete = async (record) => {
		if (!canDelete) {
			message.error("You do not have permission to delete email templates.");
			return;
		}

		if (!window.confirm("Delete this email template?")) {
			return;
		}

		try {
			await remove(
				ref(db, `talmidsdb/emailTemplates/${record.templateType}/${record.languageId}`)
			);
			message.success("Email template deleted");
		} catch (error) {
			message.error(error?.message || "Unable to delete email template.");
		}
	};

	const handleSubmit = async (values) => {
		const isEdit = Boolean(editingTemplate);
		if (isEdit && !canUpdate) {
			message.error("You do not have permission to update email templates.");
			return;
		}
		if (!isEdit && !canCreate) {
			message.error("You do not have permission to create email templates.");
			return;
		}

		const selectedLanguage = languageOptions.find(
			(option) => option.value === values.languageId
		);

		if (!selectedLanguage) {
			message.error("Selected language was not found.");
			return;
		}

		setSaving(true);
		try {
			const payload = {
				languageId: values.languageId,
				languageName: selectedLanguage.label,
				subject: values.subject?.trim() || "",
				messageHeader: values.messageHeader?.trim() || "",
				text: values.text?.trim() || "",
				html: values.html?.trim() || "",
				updatedAt: new Date().toISOString(),
			};

			await set(
				ref(db, `talmidsdb/emailTemplates/${values.templateType}/${values.languageId}`),
				payload
			);

			message.success(isEdit ? "Email template updated" : "Email template saved");
			setShowDialog(false);
			setEditingTemplate(null);
			form.resetFields();
		} catch (error) {
			message.error(error?.message || "Unable to save email template.");
		} finally {
			setSaving(false);
		}
	};

	const selectedTemplateType = Form.useWatch("templateType", form);
	const selectedMessageHeader = Form.useWatch("messageHeader", form);
	const selectedTextBody = Form.useWatch("text", form);
	const selectedGuide =
		TEMPLATE_SAVE_GUIDE[selectedTemplateType || "welcome"] ||
		TEMPLATE_SAVE_GUIDE.welcome;
	const htmlTemplateOptions =
		HTML_BODY_TEMPLATE_OPTIONS[selectedTemplateType || "welcome"] ||
		HTML_BODY_TEMPLATE_OPTIONS.welcome;

	const handleAutofillHtml = (html) => {
		form.setFieldsValue({ html });
		message.success("HTML body filled from template.");
	};

	const handleCopyHtml = async (html) => {
		try {
			await navigator.clipboard.writeText(html);
			message.success("HTML template copied.");
		} catch (error) {
			message.error("Unable to copy template.");
		}
	};

	const handlePreviewTemplate = (template) => {
		setPreviewTemplate(template);
	};

	const columns = [
		{
			title: "Template",
			dataIndex: "templateType",
			key: "templateType",
			render: (value) =>
				value === "welcome" ? "Welcome Email" : "Password Reset Email",
			sorter: (a, b) => a.templateType.localeCompare(b.templateType),
		},
		{
			title: "Language",
			dataIndex: "languageName",
			key: "languageName",
			sorter: (a, b) => a.languageName.localeCompare(b.languageName),
		},
		{
			title: "Subject",
			dataIndex: "subject",
			key: "subject",
			ellipsis: true,
		},
		{
			title: "Source",
			dataIndex: "source",
			key: "source",
			render: (value) => {
				if (value === "custom") {
					return <Tag color="green">Custom</Tag>;
				}
				if (value === "fallback") {
					return <Tag color="blue">English Fallback</Tag>;
				}
				return <Tag>Not Set</Tag>;
			},
		},
		{
			title: "Action",
			key: "action",
			render: (_, record) => (
				<TableActionMenu
					items={[
						canUpdate && {
							key: "edit",
							label: "Edit",
							onClick: () => openEditDialog(record),
						},
						canDelete && record.source === "custom" && {
							key: "delete",
							label: "Delete",
							danger: true,
							onClick: () => handleDelete(record),
						},
					]}
				/>
			),
		},
	];

	return (
		<div className="font-Nunito">
			<Dialog
				isShown={showDialog}
				title={editingTemplate ? "Edit Email Template" : "Add Email Template"}
				hasFooter={false}
				onCloseComplete={() => {
					setShowDialog(false);
					setEditingTemplate(null);
					form.resetFields();
				}}
			>
				<MDBRow className="p-2">
					<MDBCol md={12}>
						<Form
							layout="vertical"
							form={form}
							onFinish={handleSubmit}
							initialValues={{ templateType: "welcome" }}
						>
							<Form.Item
								label="Template Type"
								name="templateType"
								rules={[{ required: true, message: "Please select template type!" }]}
							>
								<Select
									placeholder="Select template type"
									disabled={Boolean(editingTemplate)}
									options={TEMPLATE_TYPE_OPTIONS}
								/>
							</Form.Item>

							<Form.Item
								label="Language"
								name="languageId"
								rules={[{ required: true, message: "Please select language!" }]}
							>
								<Select
									placeholder="Select language"
									showSearch
									disabled={Boolean(editingTemplate)}
									options={languageOptions}
									loading={languagesLoading}
									optionFilterProp="label"
								/>
							</Form.Item>

							<Form.Item
								label="Subject"
								name="subject"
								rules={[{ required: true, message: "Please enter subject!" }]}
							>
								<Input placeholder="Enter subject" />
							</Form.Item>

							<Form.Item
								label="Message Header"
								name="messageHeader"
								extra="Optional header shown in HTML layouts."
							>
								<Input placeholder="Enter message header" />
							</Form.Item>

							<Form.Item
								label="Text Body"
								name="text"
								rules={[{ required: true, message: "Please enter text body!" }]}
								extra={TEMPLATE_HINTS[selectedTemplateType || "welcome"]}
							>
								<TextArea rows={8} placeholder="Plain text email body" />
							</Form.Item>
							<div className="mb-3">
								<Popover
									trigger={["hover", "click"]}
									title="How this template is saved"
									content={renderGuideContent(selectedGuide)}
								>
									<Button appearance="minimal" type="button">
										Template Save Guide
									</Button>
								</Popover>
							</div>
							<div className="mb-3">
								<div className="mb-2" style={{ fontWeight: 600 }}>
									Starter HTML Templates
								</div>
								<div style={{ color: "#5f6b7a", marginBottom: 12 }}>
									Copy a preset or autofill the HTML body with a layout that renders the Text Body content.
								</div>
								{htmlTemplateOptions.map((template) => (
									<Card key={template.key} size="small" className="mb-2">
										<div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
											<div style={{ flex: "1 1 280px" }}>
												<div style={{ fontWeight: 600 }}>{template.label}</div>
												<div style={{ color: "#5f6b7a" }}>{template.description}</div>
											</div>
											<div className="d-flex flex-wrap" style={{ gap: 8 }}>
												<Button type="button" onClick={() => handlePreviewTemplate(template)}>
													Preview
												</Button>
												<Button type="button" onClick={() => handleCopyHtml(template.html)}>
													Copy HTML
												</Button>
												<Button
													appearance="primary"
													type="button"
													onClick={() => handleAutofillHtml(template.html)}
												>
													Autofill
												</Button>
											</div>
										</div>
									</Card>
								))}
							</div>
							<Form.Item
								label="HTML Body"
								name="html"
								extra={HTML_TEMPLATE_HINTS[selectedTemplateType || "welcome"]}
							>
								<TextArea rows={10} placeholder="Optional HTML email body" />
							</Form.Item>

							<Button appearance="primary" isLoading={saving} type="submit">
								Save Template
							</Button>
						</Form>
					</MDBCol>
				</MDBRow>
			</Dialog>
			<Dialog
				isShown={Boolean(previewTemplate)}
				title={previewTemplate ? `${previewTemplate.label} Preview` : "HTML Preview"}
				hasFooter={false}
				width={960}
				onCloseComplete={() => setPreviewTemplate(null)}
			>
				<div style={{ border: "1px solid #d9dee5", borderRadius: 8, overflow: "hidden" }}>
					<iframe
						title="Email template preview"
						srcDoc={buildHtmlPreviewDocument(
							injectPreviewVariable(
								injectTextBodyHtml(previewTemplate?.html || "", selectedTextBody),
								"messageHeader",
								selectedMessageHeader
							)
						)}
						style={{ width: "100%", height: 520, border: 0, backgroundColor: "#ffffff" }}
					/>
				</div>
			</Dialog>

			<div className="d-flex justify-content-between align-items-center mb-3">
				<div>
					<Tag color="blue">Only English has a built-in fallback. All rows here are editable.</Tag>
				</div>
				{canCreate && (
					<Button appearance="primary" iconBefore={PlusIcon} onClick={openCreateDialog}>
						Add Template
					</Button>
				)}
			</div>

			<Card>
				<Table
					rowKey="key"
					loading={templatesLoading}
					dataSource={templateRows}
					columns={columns}
					pagination={{ defaultPageSize: 20, showSizeChanger: true }}
				/>
			</Card>
		</div>
	);
};

export default EmailTemplatesLayout;
